import { useEffect, useRef, useState } from "react";

/**
 * Renders an <img> that only loads its real src when scrolled near the
 * viewport. Critical for performance when sources are heavy (base64 in
 * MongoDB). Uses the native lazy loading as a second safety net.
 */
export default function LazyImage({
    src,
    alt,
    className = "",
    eager = false,
    ...rest
}) {
    const imgRef = useRef(null);
    const [shown, setShown] = useState(eager);

    useEffect(() => {
        if (eager) return;
        const el = imgRef.current;
        if (!el) return;
        if (!("IntersectionObserver" in window)) {
            setShown(true);
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setShown(true);
                        observer.disconnect();
                    }
                });
            },
            { rootMargin: "300px" },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [eager]);

    return (
        <img
            ref={imgRef}
            src={
                shown
                    ? src
                    : "data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Crect width='4' height='5' fill='%23f0ece4'/%3E%3C/svg%3E"
            }
            alt={alt}
            loading="lazy"
            decoding="async"
            className={className}
            {...rest}
        />
    );
}
